import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { uploadFile } from "@/lib/api";
import { compressVideo } from "@/lib/videoCompress";
import { LEVELS } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, X, Film, Star, Loader2, FileCheck2, PenLine, ListChecks } from "lucide-react";

const input = "w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-slate-900 font-medium outline-none focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100 transition";
const MAX = 50 * 1024 * 1024;

function human(n) {
  return n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} Mo` : `${Math.round(n / 1024)} Ko`;
}

export default function ProofModal({ seance, open, onClose, onSubmitted, subSeances = [], initialSubSeanceId = null }) {
  const [files, setFiles] = useState([]);
  const [descriptions, setDescriptions] = useState([]);
  const [selfTab, setSelfTab] = useState("form");
  const [form, setForm] = useState({ level: 2, points_forts: "", axes: "" });
  const [selfFile, setSelfFile] = useState(null);
  const [significative, setSignificative] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [subSeanceId, setSubSeanceId] = useState(initialSubSeanceId);

  useEffect(() => {
    if (open) setSubSeanceId(initialSubSeanceId);
  }, [open, initialSubSeanceId]);

  const addFiles = (list) => {
    const arr = Array.from(list).filter((f) => {
      if (f.size > MAX && !f.type.startsWith("video/")) {
        toast.error(`${f.name} dépasse 50 Mo`);
        return false;
      }
      return true;
    });
    setFiles((prev) => [...prev, ...arr]);
    setDescriptions((prev) => [...prev, ...arr.map(() => "")]);
  };

  const removeFile = (i) => {
    setFiles(files.filter((_, j) => j !== i));
    setDescriptions(descriptions.filter((_, j) => j !== i));
  };

  const reset = () => {
    setFiles([]); setDescriptions([]); setForm({ level: 2, points_forts: "", axes: "" });
    setSelfFile(null); setSignificative(false); setStatus(""); setSelfTab("form"); setSubSeanceId(null);
  };

  const submit = async () => {
    if (subSeances.length > 0 && !subSeanceId) return toast.error("Choisis l'étape concernée par ce dépôt.");
    if (files.length === 0) return toast.error("Ajoute au moins une preuve.");
    for (let i = 0; i < files.length; i++) {
      if (files[i].type.startsWith("image/") && !descriptions[i]?.trim())
        return toast.error(`Décris la photo « ${files[i].name} » avant de déposer.`);
    }
    if (selfTab === "form" && (!form.points_forts.trim() || !form.axes.trim()))
      return toast.error("Complète ta fiche d'autoévaluation.");
    if (selfTab === "file" && !selfFile) return toast.error("Dépose ta fiche d'autoévaluation.");

    setBusy(true);
    try {
      const fileIds = [];
      const fileDescriptions = {};
      for (let i = 0; i < files.length; i++) {
        let f = files[i];
        if (f.type.startsWith("video/")) {
          setStatus(`Compression vidéo ${i + 1}/${files.length}…`);
          f = await compressVideo(f, (p) => setStatus(`Compression vidéo ${i + 1}/${files.length} — ${p}%`));
          if (f.size > MAX) { toast.error(`La vidéo ${files[i].name} dépasse 50 Mo même compressée.`); setBusy(false); setStatus(""); return; }
        }
        setStatus(`Envoi du fichier ${i + 1}/${files.length}…`);
        const up = await uploadFile(f, () => {});
        fileIds.push(up.id);
        if (files[i].type.startsWith("image/")) fileDescriptions[up.id] = descriptions[i].trim();
      }

      let selfevalFileId = null;
      if (selfTab === "file") {
        setStatus("Envoi de l'autoévaluation…");
        const up = await uploadFile(selfFile, () => {});
        selfevalFileId = up.id;
      }

      setStatus("Validation…");
      await api.post(`/seances/${seance.id}/proof`, {
        file_ids: fileIds,
        file_descriptions: fileDescriptions,
        selfeval_type: selfTab,
        selfeval_form: selfTab === "form" ? { level: Number(form.level), points_forts: form.points_forts, axes: form.axes } : null,
        selfeval_file_id: selfevalFileId,
        significative,
        sub_seance_id: subSeances.length > 0 ? subSeanceId : null,
      });
      toast.success("Séance validée — preuves déposées ✓");
      reset();
      onSubmitted && onSubmitted();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec du dépôt. Réessaie.");
    } finally {
      setBusy(false);
      setStatus("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="rounded-3xl max-w-lg p-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="proof-modal">
        <DialogHeader className="px-6 py-4 border-b border-slate-100">
          <DialogTitle className="font-display text-xl">Valider la séance — déposer mes preuves</DialogTitle>
          <p className="text-sm text-slate-500 font-medium">{seance?.title}</p>
        </DialogHeader>

        <div className="px-6 py-4 space-y-5 overflow-y-auto">
          {/* sub-seance selection */}
          {subSeances.length > 0 && (
            <div>
              <p className="text-sm font-bold text-slate-700 mb-2 inline-flex items-center gap-1.5"><ListChecks className="h-4 w-4 text-orange-500" /> Étape concernée <span className="text-red-500">*</span></p>
              <div className="grid grid-cols-1 gap-1.5" data-testid="proof-substep-selector">
                {subSeances.map((sub, idx) => (
                  <button
                    key={sub.id}
                    type="button"
                    onClick={() => setSubSeanceId(sub.id)}
                    data-testid={`proof-substep-option-${sub.id}`}
                    className={`text-left rounded-xl border-2 px-3.5 py-2.5 text-sm font-bold transition-colors ${
                      subSeanceId === sub.id ? "border-orange-400 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {idx + 1}. {sub.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* proof files */}
          <div>
            <p className="text-sm font-bold text-slate-700 mb-2">1. Mes preuves <span className="text-slate-400 font-medium">(vidéo, audio, photo, document — max 50 Mo)</span></p>
            <label data-testid="proof-add-files" className="flex flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-slate-200 hover:border-orange-300 hover:bg-orange-50 transition-colors py-6 cursor-pointer">
              <Upload className="h-6 w-6 text-orange-500" />
              <span className="text-sm font-bold text-slate-600">Choisir des fichiers</span>
              <span className="text-xs text-slate-400 font-medium">Les vidéos sont compressées automatiquement</span>
              <input type="file" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} accept="video/*,audio/*,image/*,.pdf,.doc,.docx" />
            </label>
            {files.length > 0 && (
              <div className="space-y-2 mt-2">
                {files.map((f, i) => (
                  <div key={`${f.name}-${f.size}-${i}`} className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
                    <div className="flex items-center gap-2">
                      {f.type.startsWith("video/") ? <Film className="h-4 w-4 text-orange-500 shrink-0" /> : <FileCheck2 className="h-4 w-4 text-slate-400 shrink-0" />}
                      <span className="text-sm font-bold text-slate-700 truncate flex-1">{f.name}</span>
                      <span className="text-xs text-slate-400 font-bold">{human(f.size)}</span>
                      {!busy && <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>}
                    </div>
                    {f.type.startsWith("image/") && (
                      <input
                        type="text"
                        placeholder="Décris cette photo (obligatoire)…"
                        value={descriptions[i] || ""}
                        onChange={(e) => setDescriptions((prev) => prev.map((d, j) => (j === i ? e.target.value : d)))}
                        data-testid={`proof-photo-description-${i}`}
                        className="w-full mt-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-800 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* self-eval */}
          <div>
            <p className="text-sm font-bold text-slate-700 mb-2">2. Ma fiche d'autoévaluation <span className="text-red-500">*</span> <span className="text-slate-400 font-medium">(obligatoire)</span></p>
            <div className="inline-flex bg-slate-100 rounded-full p-1 mb-3">
              <button onClick={() => setSelfTab("form")} data-testid="selfeval-tab-form" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold ${selfTab === "form" ? "bg-white text-orange-600 shadow-sm" : "text-slate-500"}`}><PenLine className="h-3.5 w-3.5" /> Remplir</button>
              <button onClick={() => setSelfTab("file")} data-testid="selfeval-tab-file" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold ${selfTab === "file" ? "bg-white text-orange-600 shadow-sm" : "text-slate-500"}`}><Upload className="h-3.5 w-3.5" /> Téléverser</button>
            </div>
            {selfTab === "form" ? (
              <div className="space-y-2">
                <div>
                  <label className="text-xs font-bold text-slate-500">Mon niveau sur cette compétence</label>
                  <div className="grid grid-cols-4 gap-1.5 mt-1">
                    {LEVELS.map((l) => (
                      <button key={l.n} onClick={() => setForm({ ...form, level: l.n })} data-testid={`selfeval-level-${l.code}`} className={`rounded-xl px-1 py-2 text-xs font-bold ${form.level === l.n ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500"}`}>{l.code}<span className="block text-[10px]">{l.label}</span></button>
                    ))}
                  </div>
                </div>
                <textarea className={input} placeholder="Mes points forts…" value={form.points_forts} onChange={(e) => setForm({ ...form, points_forts: e.target.value })} data-testid="selfeval-points" />
                <textarea className={input} placeholder="Mes axes de progrès…" value={form.axes} onChange={(e) => setForm({ ...form, axes: e.target.value })} data-testid="selfeval-axes" />
              </div>
            ) : (
              <label data-testid="selfeval-file-input" className="flex items-center gap-2 rounded-xl border-2 border-dashed border-slate-200 hover:border-orange-300 px-4 py-3 cursor-pointer">
                <Upload className="h-4 w-4 text-orange-500" />
                <span className="text-sm font-bold text-slate-600 truncate">{selfFile ? selfFile.name : "Choisir un fichier (PDF, photo…)"}</span>
                <input type="file" className="hidden" accept="image/*,.pdf,.doc,.docx" onChange={(e) => setSelfFile(e.target.files[0] || null)} />
              </label>
            )}
          </div>

          {/* significative */}
          <button onClick={() => setSignificative(!significative)} data-testid="proof-significative-toggle" className={`w-full flex items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition-colors ${significative ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}>
            <div className={`h-6 w-6 rounded-lg flex items-center justify-center shrink-0 ${significative ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-400"}`}>
              <Star className={`h-4 w-4 ${significative ? "fill-white" : ""}`} />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800">Ajouter à mes compétences significatives</p>
              <p className="text-xs text-slate-500 font-medium">Mise en avant dans mon portfolio (Bloc 2 / Bloc 3). Modifiable plus tard.</p>
            </div>
          </button>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
          <span className="text-xs font-bold text-orange-600">{status}</span>
          <button onClick={submit} disabled={busy} data-testid="proof-submit" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-full px-6 py-2.5 font-bold text-sm">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Traitement…</> : "Valider la séance"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
